import { FC, useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { CalendarToday, Event } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import CalendarComponent from '../../../../components/calendarComponent/calendarComponent';
import { ICalendarEvent, EventType } from '../../../../../apis/dashboard/dashboard.interfaces';
import { EventClickArg, EventInput } from '@fullcalendar/core';

interface CalendarWidgetProps {
  events: ICalendarEvent[];
  onEventClick?: (event: ICalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  dataTestId?: string;
  showFilters?: boolean;
}

const getEventTypeColor = (type: EventType, theme: any): string => {
  const colorMap: Record<EventType, string> = {
    [EventType.HOLIDAY]: theme.palette.error.main,
    [EventType.MEETING]: theme.palette.info.main,
    [EventType.EXAM]: theme.palette.warning.main,
    [EventType.SPORT]: theme.palette.success.main,
    [EventType.CULTURAL]: theme.palette.secondary.main,
    [EventType.WORKSHOP]: theme.palette.primary.main,
    [EventType.SEMINAR]: theme.palette.warning.dark,
    [EventType.OTHER]: theme.palette.grey[500],
  };
  return colorMap[type] || theme.palette.grey[500];
};

export const CalendarWidget: FC<CalendarWidgetProps> = ({
  events,
  onEventClick,
  onDateClick,
  dataTestId = 'calendar-widget',
  showFilters = true,
}) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const [selectedFilters, setSelectedFilters] = useState<EventType[]>([]);

  const filteredEvents = useMemo(() => {
    if (selectedFilters.length === 0) {
      return events;
    }
    return events.filter((event) => selectedFilters.includes(event.type));
  }, [events, selectedFilters]);

  // Convert ICalendarEvent to EventInput format for CalendarComponent
  const fullCalendarEvents = useMemo<EventInput[]>(() => {
    return filteredEvents.map((event) => ({
      id: event.id,
      title: event.title,
      start: event.date + (event.startTime ? `T${event.startTime}` : ''),
      end: event.endTime ? event.date + `T${event.endTime}` : undefined,
      backgroundColor: event.color || getEventTypeColor(event.type, theme),
      borderColor: event.color || getEventTypeColor(event.type, theme),
      extendedProps: {
        ...event,
      },
    }));
  }, [filteredEvents, theme]);

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilters: EventType[]
  ) => {
    setSelectedFilters(newFilters);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const eventData = clickInfo.event.extendedProps as ICalendarEvent;
    if (onEventClick && eventData) {
      onEventClick(eventData);
    }
  };

  const availableEventTypes = useMemo(() => {
    const types = new Set<EventType>();
    events.forEach((event) => types.add(event.type));
    return Array.from(types);
  }, [events]);

  return (
    <Card
      data-testid={dataTestId}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarToday sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              {translateText('dashboard.events.calendar')}
            </Typography>
          </Box>
          <Chip
            label={filteredEvents.length}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {showFilters && availableEventTypes.length > 0 && (
          <Box mb={2}>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mb: 1, display: 'block' }}
            >
              {translateText('dashboard.events.filterByType')}
            </Typography>
            <ToggleButtonGroup
              value={selectedFilters}
              onChange={handleFilterChange}
              aria-label="event type filter"
              size="small"
              sx={{
                flexWrap: 'wrap',
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                  },
                },
              }}
              data-testid={`${dataTestId}-filters`}
            >
              {availableEventTypes.map((type) => (
                <ToggleButton
                  key={type}
                  value={type}
                  aria-label={type}
                  sx={{
                    color: getEventTypeColor(type, theme),
                    '&.Mui-selected': {
                      backgroundColor: getEventTypeColor(type, theme),
                      color: theme.palette.common.white,
                    },
                  }}
                >
                  <Event sx={{ mr: 0.5, fontSize: '1rem' }} />
                  {translateText(`dashboard.events.types.${type}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        <CalendarComponent
          events={fullCalendarEvents}
          onEventClick={handleEventClick}
          onDateClick={onDateClick}
        />
      </CardContent>
    </Card>
  );
};
